import * as Config from '../../config';
import Locale from '../../utils/Locale';
import * as UUID from '../../utils/Uuid';
import { AlertInfo, AlertType, AlertRecord, HeatPumpErrorStates, AlertCategories } from "../Types";
import { AlertLogic } from './AlertLogic';
import { ContentCategories } from "../../types";


export class HeatPump extends AlertLogic {
    static type = AlertType.HeatPump;
    static subTypes = Object.values(HeatPumpErrorStates);
    static category = AlertCategories.EQUIPMENT_FAILURE;
    static contentCategories = [ ContentCategories.EQUIPMENT_FAILURE ];
    static title = Locale.gettext('Heatpump Error');

    getInfo(timeZone?: string): AlertInfo {
        const alert = this.alert;
        const config = this.allConfigs;
        
        // Same for all alert types
        let { body, device, alertData, firstReported } = this.getCommons(timeZone);
        
        // Varying across alert types
        let cardIllustration = 'pipes_emergency';
        let detailIcon = 'fan';
        let lastSeen = alert.data?.OfflineDateTime ? this.locale.formatDateTime(new Date(alert?.data?.OfflineDateTime), false) : this.locale.gettext('Never communicated');

        let errorDescription = [];
        let errorStates = (alertData.ErrorStates && alertData.ErrorStates.length > 0) ? 
            alertData.ErrorStates :
            [ { 
                HeaterErrorState: alertData.ErrorState,
                RawErrorCode: alertData.ErrorCode
            }]
        ;
        for (let errorState of errorStates) {
            let errTitle = "";
            let errDesc = "";

            switch (errorState.HeaterErrorState) {
                case HeatPumpErrorStates.HighWaterTemperature: errTitle = this.locale.gettext('High water temperature.'); break;
                case HeatPumpErrorStates.LowWaterTemperature: errTitle = this.locale.gettext('Low water temperature.'); break;
                case HeatPumpErrorStates.LowWaterFlow: errTitle = this.locale.gettext('Low water flow.'); break;
                case HeatPumpErrorStates.PoolAndSpaInputsAreBothEnabled: errTitle = this.locale.gettext('Pool and Spa inputs are both enabled.'); break;
                case HeatPumpErrorStates.WaterTemperatureSensorOpen: errTitle = this.locale.gettext('Water temperature sensor open.'); break;
                case HeatPumpErrorStates.WaterTemperatureSensorShorted: errTitle = this.locale.gettext('Water temperature sensor shorted.'); break;
                case HeatPumpErrorStates.DefrostTemperatureSensorOpen: errTitle = this.locale.gettext('Defrost temperature sensor open.'); break;
                case HeatPumpErrorStates.DefrostTemperatureSensorShorted: errTitle = this.locale.gettext('Defrost temperature sensor shorted.'); break;
                case HeatPumpErrorStates.Brownout: errTitle = this.locale.gettext('Brownout.'); break;
                case HeatPumpErrorStates.HighRefrigerant: errTitle = this.locale.gettext('High refrigerant.'); break;
                case HeatPumpErrorStates.LowRefrigerant: errTitle = this.locale.gettext('Low refrigerant.'); break;
                case HeatPumpErrorStates.FiveAlarmsInAnHour: errTitle = this.locale.gettext('Five alarms in an hour.'); break;
                case HeatPumpErrorStates.LowAmbientTemperature: errTitle = this.locale.gettext('Low ambient temperature.'); break;
                case HeatPumpErrorStates.AirFlowSwitch: errTitle = this.locale.gettext('Air flow switch.'); break;
                case HeatPumpErrorStates.IcmFault: errTitle = this.locale.gettext("Icm fault."); break;
                case HeatPumpErrorStates.AutomaticGasShutoff: errTitle = this.locale.gettext("Automatic gas shutoff."); break;
                case HeatPumpErrorStates.StackFlueHighTemp: errTitle = this.locale.gettext("Stack flue high temperature."); break;
                case HeatPumpErrorStates.StackFlueOpenShort: errTitle = this.locale.gettext("Stack flue open short."); break;
                case HeatPumpErrorStates.StackFlueRunaway: errTitle = this.locale.gettext("Stack flue runaway."); break;
                case HeatPumpErrorStates.FreezeWarning: errTitle = this.locale.gettext("Freeze warning."); break;
                case HeatPumpErrorStates.CondensateFilter: errTitle = this.locale.gettext("Condensate filter."); break;
                case HeatPumpErrorStates.CondensateFloatSwitch: errTitle = this.locale.gettext("Condensate float switch."); break;
                case HeatPumpErrorStates.ThermalFuse: errTitle = this.locale.gettext("Thermal fuse."); break;
                case HeatPumpErrorStates.HighLimitSwitch: errTitle = this.locale.gettext('High limit switch.'); break;
                case HeatPumpErrorStates.SuctionTemperatureSensorOpenShort: errTitle = this.locale.gettext("Suction temperature sensor open short."); break;
                case HeatPumpErrorStates.Defrosting: errTitle = this.locale.gettext("Defrosting."); break;
                case HeatPumpErrorStates.SilentMode: errTitle = this.locale.gettext("Silent Mode Active."); break;
                case HeatPumpErrorStates.HighPressureSwitch: errTitle = this.locale.gettext("High Pressure Switch."); break;
                case HeatPumpErrorStates.LowPressureSwitch: errTitle = this.locale.gettext("Low Pressure Switch."); break;
                case HeatPumpErrorStates.TemperatureSensor: errTitle = this.locale.gettext("Temperature Sensor."); break;
                case HeatPumpErrorStates.CommunicationError: errTitle = this.locale.gettext("Communication Error."); break;
                case HeatPumpErrorStates.RAYPAK_CWV_IN_SENSOR_FAULT:
                    errTitle = this.locale.gettext("Inlet Sensor Fault.");
                    errDesc = this.locale.gettext("Inlet Thermistors temperatures disagree by more than 3 °F. Check sensor installation, check sensor wires and connector, replace sensor.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_IN_SENSOR_OPEN:
                    errTitle = this.locale.gettext("Inlet Sensor Open.");
                    errDesc = this.locale.gettext("Inlet Thermistor sensor failed open (Below 6.3°F). Check sensor wires and connector.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_IN_SENSOR_SHORT:
                    errTitle = this.locale.gettext("Inlet Sensor Short.");
                    errDesc = this.locale.gettext("Inlet Thermistor sensor failed short (Above 186.9°F). Check sensor wires and connector.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_OUT_SENSOR_OPEN:
                    errTitle = this.locale.gettext("Outlet Sensor Open.");
                    errDesc = this.locale.gettext("Outlet Thermistor sensor failed open (Below 6.3°F). Check sensor wires and connector.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_OUT_SENSOR_SHORT:
                    errTitle = this.locale.gettext("Outlet Sensor Short.");
                    errDesc = this.locale.gettext("Outlet Thermistor sensor failed short (Above 186.9°F). Check sensor wires and connector.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_AMBIENT_SEN_OPEN:
                    errTitle = this.locale.gettext("Ambient Sensor Open.");
                    errDesc = this.locale.gettext("Ambient Thermistor sensor failed open (Below 6.3°F). Check sensor wires and connector.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_AMBIENT_SEN_SHRT:
                    errTitle = this.locale.gettext("Ambient Sensor Short.");
                    errDesc = this.locale.gettext("Ambient Thermistor sensor failed short (Above 186.9°F). Check sensor wires and connector.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_FLOW_SENSOR_FAIL:
                    errTitle = this.locale.gettext("Flow Sensor Fail.");
                    errDesc = this.locale.gettext("Flow Sensor out of range (0 to 100 GPM). Check sensor specs (1 Hz = 1 GPM), check programmed gain.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_CHECK_PUMP_FAULT:
                    errTitle = this.locale.gettext("Check Pump Fault.");
                    errDesc = this.locale.gettext("Protégé Pump controller is reporting an error. Check Protégé Pump controller.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_COIL_SENSOR_OPEN:
                    errTitle = this.locale.gettext("Coil Sensor Open.");
                    errDesc = this.locale.gettext("Coil Thermistor sensor failed open (Below 6.3°F). Check sensor wires and connector.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_COIL_SEN_SHORT:
                    errTitle = this.locale.gettext("Coil Sensor Short.");
                    errDesc = this.locale.gettext("Coil Thermistor sensor failed short (Above 186.9°F). Check sensor wires and connector.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_PUMP_CTL_FAIL:
                    errTitle = this.locale.gettext("Pump Control Fail.");
                    errDesc = this.locale.gettext("Protégé Pump Controller not responding. Check harness and connectors, check Pump board is powered.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_WCHEM_BRD_FAIL:
                    errTitle = this.locale.gettext("Water Chemistry Board Fail.");
                    errDesc = this.locale.gettext("Water Chemistry board is enabled but not responding to commands. Check harness and connectors, check Chem board is powered.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_INTERNAL_FAULT:
                    errTitle = this.locale.gettext("Internal Fault.");
                    errDesc = this.locale.gettext("Bad RAM, ROM, flame circuit, A/D converter, or safety variable corruption. Replace board.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_EEPROM_FAULT:
                    errTitle = this.locale.gettext("EEPROM Fault.");
                    errDesc = this.locale.gettext("Checksum error on Setpoints. Replace board.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_LOW_TEMP_LOCKOUT:
                    errTitle = this.locale.gettext("Low Temp Lockout.");
                    errDesc = this.locale.gettext("Water temperature below 36 °F. Wait for water temperature to increase, run pump.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_HI_PRESS_TRIP:
                    errTitle = this.locale.gettext("High Pressure Trip.");
                    errDesc = this.locale.gettext("High pressure switch is open, preventing compressor from running. Check gas pressure, check pressure sensor and wiring.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_LO_PRESS_TRIP:
                    errTitle = this.locale.gettext("Low Pressure Trip.");
                    errDesc = this.locale.gettext("Low Pressure switch is preventing compressor from running. Check gas pressure, check sensor and wiring.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_HI_PRES_LOCKOUT:
                    errTitle = this.locale.gettext("High Pressure Lockout.");
                    errDesc = this.locale.gettext("High pressure switch tripped 3 times in 1 hour. Check gas pressure, check sensor and wiring.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_LO_PRES_LOCKOUT:
                    errTitle = this.locale.gettext("Low Pressure Lockout.");
                    errDesc = this.locale.gettext("Low pressure switch tripped too many times. Check gas pressure, check sensor and wiring.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_BROWNOUT:
                    errTitle = this.locale.gettext("Brownout.");
                    errDesc = this.locale.gettext("Compressor not running because voltage too low. Check supply voltage.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_CLOCK_FAULT:
                    errTitle = this.locale.gettext("Clock Fault.");
                    errDesc = this.locale.gettext("The control board is not able to read or write clock settings. Set timer from Program menu.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_HI_TEMP_TRIP:
                    errTitle = this.locale.gettext("High Temp Trip.");
                    errDesc = this.locale.gettext("High water temperature — demand for compressor while water ≥ 106°F. Run pump, wait for water to get colder.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_HI_TEMP_LOCKOUT:
                    errTitle = this.locale.gettext("High Temp Lockout.");
                    errDesc = this.locale.gettext("High water temperature lockout — water temperature exceeded 108°F. Run pump, wait for water to get colder.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_REMOTE_ERROR:
                    errTitle = this.locale.gettext("Remote Error.");
                    errDesc = this.locale.gettext("Both Spa and Pool inputs energized. Check 3‑wire remote harness wiring.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_WATER_PS_OPEN:
                    errTitle = this.locale.gettext("Water Pressure Switch Open.");
                    errDesc = this.locale.gettext("Water pressure switch open. Check water flow, check pump.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_OUTSIDE_LOCKOUT:
                    errTitle = this.locale.gettext("Outside Lockout.");
                    errDesc = this.locale.gettext("Compressor not running because ambient too cold. Check weather, check/update outside lockout setting.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_HI_DELTA_T_FAULT:
                    errTitle = this.locale.gettext("High Delta‑T Fault.");
                    errDesc = this.locale.gettext("Absolute (Outlet‑Inlet) Temperature ≥ 20 °F. Check water flow, check pump.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_HEATING_FAIL:
                    errTitle = this.locale.gettext("Heating Fail.");
                    errDesc = this.locale.gettext("Outlet – Inlet below 2 °F. Check flow, check compressor operation.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_COOLING_FAIL:
                    errTitle = this.locale.gettext("Cooling Fail.");
                    errDesc = this.locale.gettext("Outlet is not higher than Inlet. Check flow, check compressor operation, check reversing valve.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_SOLAR_SEN_OPEN:
                    errTitle = this.locale.gettext("Solar Sensor Open.");
                    errDesc = this.locale.gettext("Solar Thermistor sensor failed open (Below 6.3 °F). Check sensor wires and connector, check solar heater settings.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_SOLAR_SEN_SHORT:
                    errTitle = this.locale.gettext("Solar Sensor Short.");
                    errDesc = this.locale.gettext("Solar Thermistor sensor failed short (Above 186.9 °F). Check sensor wires and connector, check solar heater settings.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_DEFROST_LOCKOUT:
                    errTitle = this.locale.gettext("Defrost Lockout.");
                    errDesc = this.locale.gettext("45 minutes of defrost achieved in the last 90 min. Check coil sensor, check compressor, check gas leak and gas pressures, check weather conditions.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_AUXINPUT_WARNING:
                    errTitle = this.locale.gettext("Aux Input Warning.");
                    errDesc = this.locale.gettext("Aux Input set as Warning — only show message, does not abort or cancel heat/cool operation. Check auxiliary input.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_AUX_INPUT_FAULT:
                    errTitle = this.locale.gettext("Aux Input Fault.");
                    errDesc = this.locale.gettext("Aux Input set as Interlock — control requires input to be closed, it will not heat/cool or cancel if opened. Check auxiliary input settings.");
                    break;
                case HeatPumpErrorStates.RAYPAK_CWV_SENSOR_MISPLACED:
                    errTitle = this.locale.gettext("Sensor Misplaced.");
                    errDesc = this.locale.gettext("Inlet temperature is higher than Outlet temperature while heating. Check Inlet and Outlet temperature sensors in Heat exchanger header.");
                    break;
    
                case HeatPumpErrorStates.NONE:
                    errTitle = this.locale.gettext("None.");
                    break;
                case HeatPumpErrorStates.Unknown:
                default:
                    errTitle = this.locale.gettext('Error: %1$s (%2$s)',
                        errorState.HeaterErrorState,
                        errorState.RawErrorCode
                    );
                    errDesc = errorState.Description;
                    break;
            }
            errorDescription.push({ errTitle, errDesc });
            //errorDescription.push(this.locale.formatDateTime(new Date(errorStates[0].DateTime), false, true));
        }
        if (errorDescription.length === 0) {
            // No alert.ErrorState and alertData.ErrorStates.length = 0
            errorDescription = [ { errTitle: this.locale.gettext("Unknown") } ]
        }
        const detailDescriptionArr = errorDescription.map(item => item.errTitle + (item.errDesc?.length ? "\n" + item.errDesc : ""));
        detailDescriptionArr.push(this.locale.gettext('Please review the device screen for more details.'));

        const alertInfo = AlertInfo.fromCardConfig({
            cardTitle: this.locale.gettext('Heat Pump Error'),
            cardDescription: this.locale.gettext('%1$s is reporting an error', device.Name),
            cardIllustration: cardIllustration,
            detailIcon: detailIcon,
            //detailTitle: this.locale.gettext('Device not responding'),
            detailDescription: this.locale.gettext('%1$s %2$s is reporting an error', 
                device.Name, 
                device.HardwareDevice?.UniqueBusIdentifier ? '(Serial port: ' + device.HardwareDevice?.Ports[0]?.Number + ', Bus ID : ' + device.HardwareDevice?.UniqueBusIdentifier + ')' : ''
            ),
            detailDescriptionArr,
            actionButtonTitle: '',
            resolveSummary: this.locale.gettext('%1$s error cleared', device.Name)
        });
        alertInfo.contentCategories = HeatPump.contentCategories;
        return alertInfo;
    }

    static mock(type:AlertType, config:Config.AllConfig, subType?:string):AlertRecord|AlertRecord[] {
        let subTypes = Object.values(HeatPumpErrorStates);
        if (subType) {
            subTypes = subTypes.filter(pde => pde === subType)
        }
        let device = config.Config.Devices.find(dev => dev.DeviceType === "HeatPump") || 
                        config.Config.Devices.find(dev => dev.DeviceType === "Heater");
        
        let res = [];
        for (let errorState of subTypes) {
            res.push(new AlertRecord({
                id: Math.floor(Math.random() * 100000),
                uuid: UUID.modifySection(4, UUID.modifySection(3, device.UUID, "MOCK"), "ALRT" + type),
                siteId: Math.floor(Math.random() * 100000),
                entityUuid: device?.UUID,
                bodyUuid: config.Config.BodiesOfWater[0]?.UUID,
                type: type,
                data: {
                    ErrorStartTime: new Date(Date.now() - 24*60*60*1000).toISOString(),
                    ErrorStates: [
                        {
                            HeaterErrorState: errorState,
                            RawErrorCode: Object.values(HeatPumpErrorStates).indexOf(errorState),
                            DateTime: new Date(Date.now() - 24*60*60*1000).toISOString()
                        }
                    ]
                }
            }));
        }

        return res;
    }
}